// src/ui/components/ExpenseModal.js
import { createEventExpense, uploadEventReceipt } from "../../services/expensesService.js";
import { getCurrentTeamId, getCurrentTeamMemberId } from "../../services/teamService.js";
import { listAllEvents } from "../../services/eventsService.js";
import { listVenuesByTeam } from "../../services/venuesService.js";
import { listContactsByTeam } from "../../services/contactsService.js";
import { listLineupMembers } from "../../services/lineupService.js";
import { actions } from "../../app/state.js";
import { CONTACT_TYPE_LABELS, SUPPLIER_TYPE_LABELS } from "../../domain/constants.js";
import { onSafeBackdropClick } from "../modalUtils.js";

function $(root, sel) {
    return root.querySelector(sel);
}

function todayISO() {
    return new Date().toISOString().split('T')[0];
}

const SUB_CATEGORIES = {
    "Personal": [],
    "Transporte": ["Peajes", "Avion", "Parking", "Taxi", "Alquiler", "Ave", "Gasolina", "Furgo", "Bus"],
    "Alojamiento": ["Airbnb", "Hotel", "Otro"],
    "Comidas": ["Supermercado", "Restaurante"],
    "Otros": []
};

const EXPENSE_EVENT_STAGES = new Set(["confirmed"]);

function normalizeSearchText(value) {
    return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();
}

function escapeHtml(str) {
    return String(str ?? "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#039;");
}

function eventDateTokens(dateStr) {
    const iso = String(dateStr || "").slice(0, 10);
    const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return [iso].filter(Boolean);

    const [, year, month, day] = match;
    return [
        iso,
        `${day}/${month}/${year}`,
        `${day}-${month}-${year}`,
        `${day}/${month}`,
        `${day}-${month}`,
        `${month}/${year}`,
        `${month}-${year}`,
        year,
    ];
}

function stageLabel(stage) {
    switch (stage) {
        case "accepted_pending_deposit": return "Señal pendiente";
        case "confirmed": return "Confirmado";
        case "done": return "Realizado";
        default: return stage || "";
    }
}

function eventDisplayName(event) {
    return event?.client_name_legacy || event?.name || "Evento sin nombre";
}

function eventPlaceLabel(event) {
    return event?.primary_venue_name || event?.location || event?.city || event?.region || "";
}

function eventSearchHaystack(event) {
    return normalizeSearchText([
        eventDisplayName(event),
        event?.type,
        event?.stage,
        stageLabel(event?.stage),
        eventPlaceLabel(event),
        event?.city,
        event?.region,
        ...eventDateTokens(event?.date),
    ].filter(Boolean).join(" "));
}

function venueDisplayName(venue) {
    return venue?.name || "Lugar sin nombre";
}

function venueMetaLabel(venue) {
    return [venue?.address, venue?.city, venue?.region].filter(Boolean).join(" · ");
}

function venueSearchHaystack(venue) {
    return normalizeSearchText([
        venue?.name,
        venue?.address,
        venue?.city,
        venue?.region,
        venue?.contact_notes,
        venue?.ops_notes,
    ].filter(Boolean).join(" "));
}

function contactDisplayName(contact) {
    return [contact?.name, contact?.surname].filter(Boolean).join(" ").trim()
        || contact?.company
        || "Contacto sin nombre";
}

function supplierTypes(contact) {
    const values = Array.isArray(contact?.supplier_types) && contact.supplier_types.length
        ? contact.supplier_types
        : [contact?.supplier_type].filter(Boolean);
    return [...new Set(values.filter(Boolean))];
}

function supplierTypeLabel(contact) {
    return supplierTypes(contact).map(type => SUPPLIER_TYPE_LABELS[type] || type).join(" + ");
}

function contactMetaLabel(contact) {
    const supplierLabel = contact?.is_supplier
        ? `Proveedor${supplierTypeLabel(contact) ? `: ${supplierTypeLabel(contact)}` : ""}`
        : "";
    return [
        supplierLabel,
        contact?.email,
        contact?.phone,
        contact?.company,
        CONTACT_TYPE_LABELS[contact?.type] || contact?.type,
    ].filter(Boolean).join(" Â· ");
}

function contactSearchHaystack(contact) {
    return normalizeSearchText([
        contact?.name,
        contact?.surname,
        contact?.company,
        contact?.email,
        contact?.phone,
        contact?.type,
        CONTACT_TYPE_LABELS[contact?.type],
        contact?.is_supplier ? "proveedor" : "",
        ...supplierTypes(contact),
        supplierTypeLabel(contact),
    ].filter(Boolean).join(" "));
}

function compareContactsForExpense(a, b) {
    const aSupplier = a?.is_supplier === true;
    const bSupplier = b?.is_supplier === true;
    if (aSupplier !== bSupplier) return aSupplier ? -1 : 1;

    const aType = supplierTypeLabel(a);
    const bType = supplierTypeLabel(b);
    if (aType !== bType) return aType.localeCompare(bType);

    return contactDisplayName(a).localeCompare(contactDisplayName(b));
}
function setupEventSearch(modal, sel, events, selectedEventId = null) {
    if (!sel) return;

    const today = todayISO();
    const allowedEvents = (events || [])
        .filter(event => event.id === selectedEventId
            || (EXPENSE_EVENT_STAGES.has(event.stage) && String(event.date || "").slice(0, 10) >= today))
        .sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));

    sel.innerHTML = '<option value="">— Gasto Transversal (Sin Evento) —</option>';
    allowedEvents.forEach(event => {
        const opt = document.createElement("option");
        opt.value = event.id;
        opt.textContent = `${eventDisplayName(event)} (${event.date || "Sin fecha"})`;
        sel.appendChild(opt);
    });
    sel.value = selectedEventId || "";
    sel.classList.add("hidden");

    const wrapper = document.createElement("div");
    wrapper.className = "relative";
    wrapper.innerHTML = `
        <div class="relative">
            <input id="eve-modal-event-search" type="text" autocomplete="off"
                class="w-full px-3 py-2 pl-9 rounded-xl border border-slate-200 bg-white text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                placeholder="Buscar por nombre, fecha, lugar..." />
            <i class="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none"></i>
        </div>
        <div id="eve-modal-event-results"
            class="hidden absolute z-50 left-0 right-0 mt-1 max-h-72 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg custom-scrollbar"></div>
    `;
    sel.after(wrapper);

    const input = $(modal, "#eve-modal-event-search");
    const results = $(modal, "#eve-modal-event-results");

    const selectedEvent = allowedEvents.find(event => event.id === selectedEventId);
    if (selectedEvent) input.value = `${eventDisplayName(selectedEvent)} (${selectedEvent.date || "Sin fecha"})`;

    function closeResults() {
        results.classList.add("hidden");
    }

    function selectEvent(event) {
        sel.value = event?.id || "";
        input.value = event ? `${eventDisplayName(event)} (${event.date || "Sin fecha"})` : "";
        closeResults();
    }

    function renderResults(query = "") {
        const normalizedQuery = normalizeSearchText(query);
        const matches = (normalizedQuery
            ? allowedEvents.filter(event => eventSearchHaystack(event).includes(normalizedQuery))
            : allowedEvents
        ).slice(0, 30);

        const emptyButton = `
            <button type="button" data-expense-event-empty
                class="w-full px-3 py-2 text-left text-sm font-semibold text-slate-600 hover:bg-slate-50 border-b border-slate-100">
                — Gasto Transversal (Sin Evento) —
            </button>
        `;

        if (!matches.length) {
            results.innerHTML = `
                ${emptyButton}
                <div class="px-3 py-4 text-sm text-slate-500">No hay eventos confirmados, realizados o con señal pendiente para esa busqueda.</div>
            `;
        } else {
            results.innerHTML = `
                ${emptyButton}
                ${matches.map(event => {
                    const place = eventPlaceLabel(event);
                    const meta = [event.date || "Sin fecha", stageLabel(event.stage), place].filter(Boolean).join(" · ");
                    return `
                        <button type="button" data-expense-event-id="${escapeHtml(event.id)}"
                            class="w-full px-3 py-2 text-left hover:bg-blue-50 border-b border-slate-100 last:border-b-0">
                            <span class="block text-sm font-bold text-slate-900 truncate">${escapeHtml(eventDisplayName(event))}</span>
                            <span class="block text-xs text-slate-500 truncate">${escapeHtml(meta)}</span>
                        </button>
                    `;
                }).join("")}
            `;
        }
        results.classList.remove("hidden");
    }

    input.addEventListener("focus", () => renderResults(input.value));
    input.addEventListener("input", () => {
        sel.value = "";
        renderResults(input.value);
    });

    results.addEventListener("mousedown", (ev) => {
        ev.preventDefault();
        const emptyBtn = ev.target.closest("[data-expense-event-empty]");
        if (emptyBtn) {
            selectEvent(null);
            return;
        }

        const eventBtn = ev.target.closest("[data-expense-event-id]");
        if (!eventBtn) return;
        selectEvent(allowedEvents.find(event => event.id === eventBtn.getAttribute("data-expense-event-id")));
    });

    document.addEventListener("mousedown", (ev) => {
        if (!wrapper.contains(ev.target)) closeResults();
    });
}

function setupContactSearch(modal, contactSelect, contacts) {
    if (!contactSelect) return;

    const contactRows = (contacts || [])
        .slice()
        .sort(compareContactsForExpense);

    contactSelect.innerHTML = '<option value="">— Ninguno —</option>';
    contactRows.forEach(contact => {
        const opt = document.createElement("option");
        opt.value = contact.id;
        const supplierLabel = contact?.is_supplier
            ? `Proveedor: ${supplierTypeLabel(contact) || "Sin tipo"}`
            : CONTACT_TYPE_LABELS[contact?.type] || "Contacto";
        opt.textContent = `${contactDisplayName(contact)} (${supplierLabel})`;
        contactSelect.appendChild(opt);
    });
    contactSelect.classList.add("hidden");

    const wrapper = document.createElement("div");
    wrapper.className = "relative";
    wrapper.innerHTML = `
        <div class="relative">
            <input id="eve-modal-contact-search" type="text" autocomplete="off"
                class="w-full px-3 py-2 pl-9 rounded-xl border border-slate-200 bg-white text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                placeholder="Buscar por proveedor, email, telefono..." />
            <i class="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none"></i>
        </div>
        <div id="eve-modal-contact-results"
            class="hidden absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg custom-scrollbar"></div>
    `;
    contactSelect.after(wrapper);

    const input = $(modal, "#eve-modal-contact-search");
    const results = $(modal, "#eve-modal-contact-results");

    function closeResults() {
        results.classList.add("hidden");
    }

    function selectContact(contact) {
        contactSelect.value = contact?.id || "";
        input.value = contact ? contactDisplayName(contact) : "";
        closeResults();
    }

    function renderResults(query = "") {
        const normalizedQuery = normalizeSearchText(query);
        const matches = (normalizedQuery
            ? contactRows.filter(contact => contactSearchHaystack(contact).includes(normalizedQuery))
            : contactRows
        ).slice(0, 30);

        const emptyButton = `
            <button type="button" data-expense-contact-empty
                class="w-full px-3 py-2 text-left text-sm font-semibold text-slate-600 hover:bg-slate-50 border-b border-slate-100">
                — Ninguno —
            </button>
        `;

        if (!matches.length) {
            results.innerHTML = `
                ${emptyButton}
                <div class="px-3 py-4 text-sm text-slate-500">No hay proveedores/contactos para esa busqueda.</div>
            `;
        } else {
            results.innerHTML = `
                ${emptyButton}
                ${matches.map(contact => {
                    const meta = contactMetaLabel(contact);
                    const supplierBadge = contact?.is_supplier
                        ? `<span class="inline-flex items-center rounded-md bg-blue-50 px-1.5 py-0.5 text-[10px] font-bold text-blue-700 border border-blue-100">Proveedor</span>`
                        : "";
                    return `
                        <button type="button" data-expense-contact-id="${escapeHtml(contact.id)}"
                            class="w-full px-3 py-2 text-left hover:bg-blue-50 border-b border-slate-100 last:border-b-0">
                            <span class="flex items-center gap-2 min-w-0">
                                <span class="block text-sm font-bold text-slate-900 truncate">${escapeHtml(contactDisplayName(contact))}</span>
                                ${supplierBadge}
                            </span>
                            <span class="block text-xs text-slate-500 truncate">${escapeHtml(meta || "Sin email/telefono")}</span>
                        </button>
                    `;
                }).join("")}
            `;
        }
        results.classList.remove("hidden");
    }

    input.addEventListener("focus", () => renderResults(input.value));
    input.addEventListener("input", () => {
        contactSelect.value = "";
        renderResults(input.value);
    });

    results.addEventListener("mousedown", (ev) => {
        ev.preventDefault();
        const emptyBtn = ev.target.closest("[data-expense-contact-empty]");
        if (emptyBtn) {
            selectContact(null);
            return;
        }

        const contactBtn = ev.target.closest("[data-expense-contact-id]");
        if (!contactBtn) return;
        selectContact(contactRows.find(contact => contact.id === contactBtn.getAttribute("data-expense-contact-id")));
    });

    document.addEventListener("mousedown", (ev) => {
        if (!wrapper.contains(ev.target)) closeResults();
    });
}

function setupVenueSearch(modal, venueSelect, venues) {
    if (!venueSelect) return;

    const venueRows = (venues || [])
        .slice()
        .sort((a, b) => venueDisplayName(a).localeCompare(venueDisplayName(b)));

    venueSelect.innerHTML = '<option value="">— Ninguno —</option>';
    venueRows.forEach(venue => {
        const opt = document.createElement("option");
        opt.value = venue.id;
        opt.textContent = venueDisplayName(venue);
        venueSelect.appendChild(opt);
    });
    venueSelect.classList.add("hidden");

    const wrapper = document.createElement("div");
    wrapper.className = "relative";
    wrapper.innerHTML = `
        <div class="relative">
            <input id="eve-modal-venue-search" type="text" autocomplete="off"
                class="w-full px-3 py-2 pl-9 rounded-xl border border-slate-200 bg-white text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                placeholder="Buscar por lugar, direccion, ciudad..." />
            <i class="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none"></i>
        </div>
        <div id="eve-modal-venue-results"
            class="hidden absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg custom-scrollbar"></div>
    `;
    venueSelect.after(wrapper);

    const input = $(modal, "#eve-modal-venue-search");
    const results = $(modal, "#eve-modal-venue-results");

    function closeResults() {
        results.classList.add("hidden");
    }

    function selectVenue(venue) {
        venueSelect.value = venue?.id || "";
        input.value = venue ? venueDisplayName(venue) : "";
        closeResults();
    }

    function renderResults(query = "") {
        const normalizedQuery = normalizeSearchText(query);
        const matches = (normalizedQuery
            ? venueRows.filter(venue => venueSearchHaystack(venue).includes(normalizedQuery))
            : venueRows
        ).slice(0, 30);

        const emptyButton = `
            <button type="button" data-expense-venue-empty
                class="w-full px-3 py-2 text-left text-sm font-semibold text-slate-600 hover:bg-slate-50 border-b border-slate-100">
                — Ninguno —
            </button>
        `;

        if (!matches.length) {
            results.innerHTML = `
                ${emptyButton}
                <div class="px-3 py-4 text-sm text-slate-500">No hay lugares para esa busqueda.</div>
            `;
        } else {
            results.innerHTML = `
                ${emptyButton}
                ${matches.map(venue => {
                    const meta = venueMetaLabel(venue);
                    return `
                        <button type="button" data-expense-venue-id="${escapeHtml(venue.id)}"
                            class="w-full px-3 py-2 text-left hover:bg-blue-50 border-b border-slate-100 last:border-b-0">
                            <span class="block text-sm font-bold text-slate-900 truncate">${escapeHtml(venueDisplayName(venue))}</span>
                            <span class="block text-xs text-slate-500 truncate">${escapeHtml(meta || "Sin direccion")}</span>
                        </button>
                    `;
                }).join("")}
            `;
        }
        results.classList.remove("hidden");
    }

    input.addEventListener("focus", () => renderResults(input.value));
    input.addEventListener("input", () => {
        venueSelect.value = "";
        renderResults(input.value);
    });

    results.addEventListener("mousedown", (ev) => {
        ev.preventDefault();
        const emptyBtn = ev.target.closest("[data-expense-venue-empty]");
        if (emptyBtn) {
            selectVenue(null);
            return;
        }

        const venueBtn = ev.target.closest("[data-expense-venue-id]");
        if (!venueBtn) return;
        selectVenue(venueRows.find(venue => venue.id === venueBtn.getAttribute("data-expense-venue-id")));
    });

    document.addEventListener("mousedown", (ev) => {
        if (!wrapper.contains(ev.target)) closeResults();
    });
}

async function openCreateEventExpenseModal({ event_id = null, onSuccess = null, defaultOwner = null, lockOwner = false } = {}) {
    const tpl = document.getElementById("event-expense-modal-template");
    if (!tpl) {
        console.error("Template event-expense-modal-template no encontrado");
        return;
    }

    const node = tpl.content.cloneNode(true);
    const modal = node.querySelector(".modal-backdrop");
    modal?.classList.add("event-expense-entry-modal");
    document.body.appendChild(modal);

    const sel = $(modal, "#eve-modal-event-id");
    const amountInput = $(modal, "#eve-modal-amount");
    const dateInput = $(modal, "#eve-modal-date");
    const catInput = $(modal, "#eve-modal-cat");
    const subcatContainer = $(modal, "#eve-modal-subcat-container");
    const subcatInput = $(modal, "#eve-modal-subcat");
    const noteInput = $(modal, "#eve-modal-note");
    const saveBtn = $(modal, "#eve-modal-save");
    const cancelBtn = $(modal, "#eve-modal-cancel");
    const receiptFileInput = $(modal, "#eve-modal-receipt-file");
    const receiptPreviewContainer = $(modal, "#receipt-preview-container");
    const receiptPreviewName = $(modal, "#receipt-preview-name");
    const receiptPreviewStatus = $(modal, "#receipt-preview-status");
    let selectedFile = null;

    // Transversal Fields (Injected after Event selector)
    let venueSelect = null;
    let contactSelect = null;

    if (sel && sel.parentElement) {
        // Venue Selector
        const venueDiv = document.createElement("div");
        venueDiv.innerHTML = `
            <label class="block text-sm text-slate-600 mb-1">Vincular a Lugar (Opcional)</label>
            <select id="eve-modal-venue-id" class="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white">
                <option value="">— Ninguno —</option>
            </select>
        `;
        sel.parentElement.after(venueDiv);
        venueSelect = $(modal, "#eve-modal-venue-id");

        // Contact Selector
        const contactDiv = document.createElement("div");
        contactDiv.innerHTML = `
            <label class="block text-sm text-slate-600 mb-1">Vincular a Proveedor/Contacto (Opcional)</label>
            <select id="eve-modal-contact-id" class="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white">
                <option value="">— Ninguno —</option>
            </select>
        `;
        venueDiv.after(contactDiv);
        contactSelect = $(modal, "#eve-modal-contact-id");
    }

    // Owner fields (dynamically created if not in template)
    let ownerSelect = $(modal, "#eve-modal-owner");
    let memberWrapper = $(modal, "#eve-modal-member-wrapper");
    let memberSelect = $(modal, "#eve-modal-member");
    let reimbInfo = $(modal, "#eve-modal-reimbursable-info");

    // If owner fields don't exist in template, inject them before the category
    if (!ownerSelect) {
        const formBody = catInput?.closest(".space-y-3") || catInput?.parentElement?.parentElement;
        if (formBody && catInput?.parentElement) {
            // Owner type selector
            const ownerDiv = document.createElement("div");
            ownerDiv.innerHTML = `
                <label class="block text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">Tipo de gasto</label>
                <select id="eve-modal-owner" class="w-full px-4 py-2.5 border border-slate-200 rounded-xl bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all">
                    <option value="empresa">🏢 Empresa</option>
                    <option value="personal">👤 Personal</option>
                </select>
            `;
            catInput.parentElement.before(ownerDiv);
            ownerSelect = $(modal, "#eve-modal-owner");

            // Member selector (hidden by default)
            const memberDiv = document.createElement("div");
            memberDiv.id = "eve-modal-member-wrapper";
            memberDiv.className = "hidden";
            memberDiv.innerHTML = `
                <label class="block text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">Quién pagó</label>
                <select id="eve-modal-member" class="w-full px-4 py-2.5 border border-slate-200 rounded-xl bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all">
                    <option value="">— Selecciona miembro —</option>
                </select>
            `;
            ownerDiv.after(memberDiv);
            memberWrapper = memberDiv;
            memberSelect = $(modal, "#eve-modal-member");

            const reimbDiv = document.createElement("div");
            reimbDiv.id = "eve-modal-reimbursable-info";
            reimbDiv.className = "hidden rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-900";
            reimbDiv.innerHTML = `
                <div class="flex gap-2">
                    <i class="fa-solid fa-circle-info mt-0.5 text-blue-500"></i>
                    <div>
                        <div class="font-bold">Gasto operativo reembolsable</div>
                        <div class="mt-0.5 text-blue-800/80">Si lo paga una persona, la empresa se lo devolvera. Si no es reembolsable, no hace falta registrarlo aqui.</div>
                    </div>
                </div>
            `;
            memberDiv.after(reimbDiv);
            reimbInfo = reimbDiv;
        }
    }

    // Wire owner toggle
    ownerSelect?.addEventListener("change", () => {
        const isPersonal = ownerSelect.value === "personal";
        memberWrapper?.classList.toggle("hidden", !isPersonal);
        reimbInfo?.classList.toggle("hidden", !isPersonal);
        if (!isPersonal) {
            if (memberSelect) memberSelect.value = "";
        }
    });

    // Initial values
    dateInput.value = todayISO();
    if (defaultOwner && ownerSelect) {
        ownerSelect.value = defaultOwner;
        ownerSelect.disabled = !!lockOwner;
        ownerSelect.dispatchEvent(new Event("change"));
    }

    // Logic for subcategories
    catInput.addEventListener("change", () => {
        const cat = catInput.value;
        const subs = SUB_CATEGORIES[cat] || [];

        // Clear subs
        if (subcatInput) {
            subcatInput.innerHTML = '<option value="">— Sin subcategoría —</option>';

            if (subs.length > 0) {
                subs.forEach(s => {
                    const opt = document.createElement("option");
                    opt.value = s;
                    opt.textContent = s;
                    subcatInput.appendChild(opt);
                });
                subcatContainer?.classList.remove("hidden");
            } else {
                subcatContainer?.classList.add("hidden");
            }
        }
    });

    // Load team members for paid_by dropdown
    try {
        const teamId = await getCurrentTeamId();
        const [members, currentMemberId] = await Promise.all([
            listLineupMembers(teamId),
            getCurrentTeamMemberId(teamId)
        ]);
        if (memberSelect) {
            members.filter(m => m.is_active).forEach(m => {
                const opt = document.createElement("option");
                opt.value = m.id;
                opt.textContent = m.display_name;
                memberSelect.appendChild(opt);
            });
            if (currentMemberId) memberSelect.value = currentMemberId;
        }
    } catch (e) {
        console.warn("Error loading team members for expense modal:", e);
    }

    // Load Events, Venues and Contacts
    try {
        const teamId = await getCurrentTeamId();
        const [events, venues, contacts] = await Promise.all([
            listAllEvents(),
            listVenuesByTeam(teamId),
            listContactsByTeam(teamId)
        ]);

        // Events
        setupEventSearch(modal, sel, events, event_id);

        // Venues
        if (venueSelect) {
            setupVenueSearch(modal, venueSelect, venues);
        }

        // Contacts
        if (contactSelect) {
            setupContactSearch(modal, contactSelect, contacts);
        }
    } catch (err) {
        console.error("Error cargando datos para el modal de gasto", err);
    }

    receiptFileInput?.addEventListener("change", (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        selectedFile = file;
        receiptPreviewContainer?.classList.remove("hidden");
        if (receiptPreviewName) receiptPreviewName.textContent = file.name || "Ticket adjunto";
        if (receiptPreviewStatus) {
            const kb = Math.max(1, Math.round((file.size || 0) / 1024));
            receiptPreviewStatus.textContent = `Se subira al guardar. ${kb} KB`;
        }
    });

    const close = () => modal.remove();

    cancelBtn.addEventListener("click", close);
    onSafeBackdropClick(modal, close);

    saveBtn.addEventListener("click", async () => {
        const teamId = await getCurrentTeamId();
        const expOwner = ownerSelect?.value || (defaultOwner === "personal" ? "personal" : "empresa");
        const paidBy = expOwner === "personal" ? (memberSelect?.value || null) : null;
        const isReimb = expOwner === "personal";

        const payload = {
            team_id: teamId,
            event_id: sel.value || null,
            venue_id: venueSelect?.value || null,
            contact_id: contactSelect?.value || null,
            amount: Number(amountInput.value),
            expense_date: dateInput.value || todayISO(),
            expense_category: catInput.value,
            expense_subcategory: subcatInput?.value || null,
            note: noteInput.value,
            expense_owner: expOwner,
            paid_by_member_id: paidBy,
            is_reimbursable: isReimb,
            receipt_url: null
        };

        if (!payload.amount || !payload.expense_category) {
            actions.addToast?.({ type: 'error', message: 'Importe y Categoría son obligatorios' });
            return;
        }

        if (expOwner === "personal" && !paidBy) {
            actions.addToast?.({ type: 'error', message: 'Selecciona quién pagó el gasto' });
            return;
        }

        try {
            saveBtn.disabled = true;
            saveBtn.innerHTML = '<i class="fa-solid fa-circle-notch fa-spin mr-2"></i>Guardando...';

            if (selectedFile) {
                saveBtn.innerHTML = '<i class="fa-solid fa-cloud-arrow-up fa-spin mr-2"></i>Subiendo ticket...';
                payload.receipt_url = await uploadEventReceipt(selectedFile);
            }

            await createEventExpense(payload);
            actions.addToast?.({ type: 'success', message: 'Gasto registrado correctamente' });
            close();

            if (typeof onSuccess === 'function') onSuccess();
        } catch (err) {
            console.error(err);
            actions.addToast?.({ type: 'error', message: 'Error al registrar el gasto: ' + err.message });
            saveBtn.disabled = false;
            saveBtn.innerHTML = 'Guardar Gasto';
        }
    });

    return modal;
}

export default {
    openCreateEventExpenseModal
};
